// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { type PolarisEnv, parsePolarisEnv } from '@polarissleep/shared';

/**
 * Polaris environment + tenant Organization context for bot code.
 *
 * In production, `deploy-bots.ts` prepends a two-line prelude to each bot's
 * compiled JavaScript source before uploading it to Medplum:
 *
 *   globalThis.POLARIS_ENV = "<env>";
 *   globalThis.POLARIS_TENANT_ORG_ID = "<envOrgId>";
 *   globalThis.POLARIS_SCHEDULING_ENGINE = "medplum-v2";
 *   globalThis.POLARIS_MIDDLEMAN_API_URL = "https://…";
 *   globalThis.POLARIS_PROVIDER_APP_BASE_URL = "https://…";
 *
 * Every bot in the shared Medplum Project therefore carries its own baked-in
 * environment config — no risk of one environment's configuration trampling
 * another's. The tenant org
 * is used by `bots/utils/tenant.ts#withTenantScope` to stamp `meta.account`
 * on every resource a bot creates, which in turn is what Medplum filters on
 * when evaluating `_compartment=Organization/<id>` in a Subscription criteria.
 *
 * In tests, the same globals are seeded from `src/test.setup.ts` so tests
 * mirror runtime behaviour exactly. Use `setPolarisEnv()` /
 * `setPolarisTenantOrgId()` for test-specific overrides.
 */

interface PolarisContextHolder {
  POLARIS_ENV?: string;
  POLARIS_TENANT_ORG_ID?: string;
  POLARIS_SCHEDULING_ENGINE?: string;
  POLARIS_MIDDLEMAN_API_URL?: string;
  POLARIS_PROVIDER_APP_BASE_URL?: string;
}

let envTestOverride: PolarisEnv | undefined;
let tenantOrgIdTestOverride: string | undefined;
let schedulingEngineTestOverride: SchedulingEngine | undefined;

export type SchedulingEngine = 'legacy' | 'medplum-v2';

export function setPolarisEnv(env: PolarisEnv): void {
  envTestOverride = env;
}

export function clearPolarisEnv(): void {
  envTestOverride = undefined;
}

export function getPolarisEnv(): PolarisEnv {
  if (envTestOverride) {
    return envTestOverride;
  }
  const fromGlobal = (globalThis as PolarisContextHolder).POLARIS_ENV;
  if (!fromGlobal) {
    throw new Error(
      "POLARIS_ENV is not set. In production, deploy-bots.ts injects it into each bot's compiled source. " +
        'In tests, call setPolarisEnv() from a setup hook.'
    );
  }
  return parsePolarisEnv(fromGlobal);
}

export function setPolarisTenantOrgId(orgId: string): void {
  tenantOrgIdTestOverride = orgId;
}

export function clearPolarisTenantOrgId(): void {
  tenantOrgIdTestOverride = undefined;
}

/**
 * The tenant Organization id for the current env. Used as the
 * `meta.account` value on every resource created by a bot, and by
 * Subscription criteria (`_compartment=Organization/<id>`) to scope which
 * resource changes fire which bots.
 */
export function getPolarisTenantOrgId(): string {
  if (tenantOrgIdTestOverride) {
    return tenantOrgIdTestOverride;
  }
  const fromGlobal = (globalThis as PolarisContextHolder).POLARIS_TENANT_ORG_ID;
  if (!fromGlobal) {
    throw new Error(
      "POLARIS_TENANT_ORG_ID is not set. In production, deploy-bots.ts injects it into each bot's compiled source. " +
        'In tests, call setPolarisTenantOrgId() from a setup hook.'
    );
  }
  return fromGlobal;
}

export function setPolarisSchedulingEngine(engine: SchedulingEngine): void {
  if (engine !== 'medplum-v2') {
    throw new Error('Only medplum-v2 scheduling is supported.');
  }
  schedulingEngineTestOverride = engine;
}

export function clearPolarisSchedulingEngine(): void {
  schedulingEngineTestOverride = undefined;
}

/** Returns the scheduling implementation selected for this deployed bot. */
export function getPolarisSchedulingEngine(): SchedulingEngine {
  if (schedulingEngineTestOverride) {
    return schedulingEngineTestOverride;
  }
  const configured = (globalThis as PolarisContextHolder).POLARIS_SCHEDULING_ENGINE ?? 'medplum-v2';
  if (configured === 'medplum-v2') {
    return configured;
  }
  throw new Error(`POLARIS_SCHEDULING_ENGINE is invalid: ${configured}. Only medplum-v2 is supported.`);
}

export function getPolarisMiddlemanApiUrl(): string | undefined {
  return (globalThis as PolarisContextHolder).POLARIS_MIDDLEMAN_API_URL;
}

export function getPolarisProviderAppBaseUrl(): string | undefined {
  return (globalThis as PolarisContextHolder).POLARIS_PROVIDER_APP_BASE_URL;
}
